import {
  Autocomplete,
  Box,
  createFilterOptions,
  debounce,
  FormControlLabel,
  Grid,
  ListItem,
  ListItemText,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { useEffect, useMemo, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import AutocompleteGroupedList from "../../../common/components/functional/AutocompleteGroupedList/AutocompleteGroupedList";
import CommonTextField from "../../../common/components/functional/commonTextField/CommonTextField";
import InputHelperText from "../../../common/components/functional/error/InputHelperText";
import InfoTooltip from "../../../common/components/functional/InfoTooltip/InfoTooltip";
import getLocale from "../../../common/helpers/getLocale";
import isEmpty from "../../../common/helpers/isEmpty";
import { CustomerOrgCompany } from "../../../common/types/apiTypes";
import { customerOrgAndCompAutocomplete } from "../../customer/customerAPI";
import {
  fetchWorkLocationsAndContractsByOrgId,
  selectActiveWorkLocations,
} from "../../customer/customerWorkLocationsSlice";
import { WorkLocation } from "../../customer/types/queries";
import { WorkTitle } from "../../orders/orderAPI";
import { fetchTalentSelfServiceOrderPermissions } from "../../talents/talentsSlice";
import { TalentSelfServiceOrderPermission } from "../../talents/types/queries";
import { fetchWorkTitles } from "../../workTitles/workTitleSlice";
import { BlacklistFormValues } from "./blacklistMappers";
import { BLOCKING_PARTIES } from "./constants";

type Props = {
  isEditing?: boolean;
};

type BlacklistEntityOption = { id: string; name: string } | null;

export const AddBlacklistForm = ({ isEditing = false }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const locale = getLocale();

  const { localized } = useAppSelector((state) => state);
  const workLocationsInStore = useAppSelector((state) =>
    selectActiveWorkLocations(state),
  );

  const [customerCompanyValue, setCustomerCompanyValue] = useState("");
  const [customerCompanies, setCustomerCompanies] = useState<
    CustomerOrgCompany[]
  >([]);
  const [availableWorkTitles, setAvailableWorkTitles] = useState<WorkTitle[]>(
    [],
  );
  const [workLocationOptions, setWorkLocationOptions] = useState<
    WorkLocation[]
  >([]);
  const [permissions, setPermissions] = useState<
    TalentSelfServiceOrderPermission[]
  >([]);

  const { talentId } = useParams();

  useEffect(() => {
    if (customerCompanyValue.length >= 2) {
      customerOrgAndCompAutocomplete({ name: customerCompanyValue }).then(
        (res) => setCustomerCompanies(res),
      );
    }
  }, [customerCompanyValue]);

  const handleCustomerCompanyValChange = debounce(
    (value: string) => setCustomerCompanyValue(value),
    400,
  );

  const {
    control,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext<BlacklistFormValues>();

  const { customerCompany, customerOrganization, workTitles, workLocation } =
    watch();

  useEffect(() => {
    if (!talentId) return;
    dispatch(fetchTalentSelfServiceOrderPermissions({ talentId })).then(
      (action) => {
        if (isFulfilled(action)) {
          setPermissions(action.payload);
        }
      },
    );
  }, [dispatch, talentId]);

  // Fetch available work titles when component is mounted
  useEffect(() => {
    if (!isEmpty(availableWorkTitles)) return;
    dispatch(fetchWorkTitles({ language: locale.code })).then((action) => {
      if (isFulfilled(action)) {
        const sortedWorkTitles = action.payload
          .sort((a, b) => a.name.localeCompare(b.name))
          .sort((a, b) => {
            if (a.fieldOfWorkId && b.fieldOfWorkId) {
              return a.fieldOfWorkId.localeCompare(b.fieldOfWorkId);
            }
            return 0;
          });
        setAvailableWorkTitles(sortedWorkTitles);
      }
    });
  }, [availableWorkTitles, dispatch, locale.code]);

  // Fetch work locations when customer organization is selected.
  useEffect(() => {
    if (!customerOrganization) return;
    dispatch(
      fetchWorkLocationsAndContractsByOrgId({ id: customerOrganization.id }),
    );
  }, [customerOrganization, dispatch]);

  // Update work location options when new ones are fetched
  useEffect(() => {
    if (!workLocationsInStore) return;
    setWorkLocationOptions(workLocationsInStore);
  }, [workLocationsInStore]);

  const permissionConflict = useMemo(() => {
    if (!customerOrganization?.id || !workLocation?.id || !workTitles?.length) {
      return false;
    }

    return permissions.some(
      (p: TalentSelfServiceOrderPermission) =>
        p.customerOrganizationId === customerOrganization.id &&
        p.workLocationId === workLocation.id &&
        p.workTitleId &&
        workTitles.some((wt: WorkTitle) => wt.id === p.workTitleId),
    );
  }, [customerOrganization, permissions, workLocation, workTitles]);

  const customerCompanyOptions = useMemo(() => {
    if (!customerCompanies.length) return [];
    return customerCompanies.flatMap((item) => ({
      id: item.id,
      name: item.name,
    }));
  }, [customerCompanies]);

  const customerOrgOptions = useMemo(() => {
    if (!customerCompany || !customerCompanies) return [];
    return (
      customerCompanies.find((company) => company.id === customerCompany.id)
        ?.customerOrganizations || []
    );
  }, [customerCompany, customerCompanies]);

  useEffect(() => {
    if (!customerOrganization) {
      setWorkLocationOptions([]);
    }
  }, [customerOrganization]);

  const handleCustomerCompanyChange = (
    currentValue: BlacklistEntityOption,
    newValue: BlacklistEntityOption,
    onChange: (value: BlacklistEntityOption) => void,
  ) => {
    if (currentValue?.id !== newValue?.id) {
      setWorkLocationOptions([]);
      setValue("customerOrganization", null);
      setValue("workLocation", null);
    }
    onChange(newValue);
  };

  const handleCustomerOrganizationChange = (
    currentValue: BlacklistEntityOption,
    newValue: BlacklistEntityOption,
    onChange: (value: BlacklistEntityOption) => void,
  ) => {
    if (currentValue?.id !== newValue?.id) {
      setWorkLocationOptions([]);
      setValue("workLocation", null);
    }
    onChange(newValue);
  };

  return (
    <>
      <Grid container rowSpacing={1} columnSpacing={2}>
        <Grid
          container
          item
          xs={3}
          justifyContent="flex-end"
          alignItems="center"
        >
          <Typography color="textSecondary">
            {t("talentProfile:blacklister")} *
          </Typography>
        </Grid>
        <Grid item xs={9}>
          <Controller
            control={control}
            name="blockingParty"
            rules={{ required: true }}
            render={({ field }) => (
              <RadioGroup value={field.value} onChange={field.onChange}>
                {BLOCKING_PARTIES.map((party) => (
                  <FormControlLabel
                    key={party.name}
                    value={party.name}
                    control={<Radio />}
                    label={t(`talentProfile:${party.label}`)}
                    disabled={isEditing}
                  />
                ))}
              </RadioGroup>
            )}
          />
        </Grid>
        <Grid
          container
          item
          xs={3}
          justifyContent="flex-end"
          alignItems="center"
        >
          <Typography color="textSecondary">
            {t("talentProfile:customerCompany")} *
          </Typography>
        </Grid>
        <Grid item xs={9}>
          <Controller
            control={control}
            name="customerCompany"
            rules={{ required: true }}
            render={({ field }) => (
              <Autocomplete
                value={field.value}
                disabled={isEditing}
                sx={{
                  backgroundColor: "rgba(249, 249, 250, 0.6)",
                }}
                options={customerCompanyOptions}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={(option) => option.name}
                filterOptions={createFilterOptions({
                  stringify: (option) => JSON.stringify(option),
                })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={t("talentProfile:selectCustomerCompany")}
                    size="small"
                    name="customerOrgCompany"
                    error={!!errors.customerCompany}
                  />
                )}
                onChange={(_, newValue) =>
                  handleCustomerCompanyChange(
                    field.value,
                    newValue,
                    field.onChange,
                  )
                }
                onInputChange={(_, newValue: string) => {
                  if (!!newValue) {
                    handleCustomerCompanyValChange(newValue);
                  }
                }}
              />
            )}
          />
        </Grid>
        <Grid
          container
          item
          xs={3}
          justifyContent="flex-end"
          alignItems="center"
        >
          <Typography color="textSecondary">
            {t("talentProfile:organization")}
          </Typography>
          <InfoTooltip
            style={{ marginLeft: "8px" }}
            title={t("talentProfile:blacklistCustomerOrgInfo")}
          />
        </Grid>
        <Grid item xs={9}>
          <Controller
            control={control}
            name="customerOrganization"
            render={({ field }) => (
              <Autocomplete
                value={field.value}
                disabled={!customerCompany}
                sx={{
                  backgroundColor: "rgba(249, 249, 250, 0.6)",
                }}
                options={customerOrgOptions}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={(option) => option.name}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={t("talentProfile:selectOrganization")}
                    size="small"
                    name="customerOrgCompany"
                    error={!!errors.customerOrganization}
                  />
                )}
                onChange={(_, newValue) =>
                  handleCustomerOrganizationChange(
                    field.value,
                    newValue,
                    field.onChange,
                  )
                }
                onInputChange={(_, newValue: string) => {
                  if (!!newValue) {
                    handleCustomerCompanyValChange(newValue);
                  }
                }}
              />
            )}
          />
        </Grid>
        <Grid
          container
          item
          xs={3}
          justifyContent="flex-end"
          alignItems="center"
        >
          <Typography color="textSecondary">{t("workLocation")}</Typography>
          <InfoTooltip
            style={{ marginLeft: "8px" }}
            title={t("talentProfile:blacklistWorkLocationInfo")}
          />
        </Grid>
        <Grid item xs={9}>
          <Controller
            control={control}
            name="workLocation"
            render={({ field }) => (
              <Autocomplete
                value={field.value}
                disabled={!customerOrganization}
                sx={{
                  backgroundColor: "rgba(249, 249, 250, 0.6)",
                }}
                options={workLocationOptions}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={(option) => option.name}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={t("talentProfile:selectWorkLocation")}
                    size="small"
                    name="workLocation"
                    error={!!errors.workLocation}
                  />
                )}
                onChange={(_, newValue: WorkLocation | null) =>
                  field.onChange(newValue)
                }
              />
            )}
          />
        </Grid>
        <Grid
          container
          item
          xs={3}
          justifyContent="flex-end"
          alignItems="center"
        >
          <Typography color="textSecondary">{t("workTitles")}</Typography>
          <InfoTooltip
            style={{ marginLeft: "8px" }}
            title={t("talentProfile:blacklistWorktitleInfo")}
          />
        </Grid>
        <Grid item xs={9}>
          <Controller
            control={control}
            name="workTitles"
            render={({ field }) => (
              <Autocomplete
                fullWidth
                multiple
                value={field.value}
                disabled={!customerCompany}
                groupBy={(option) =>
                  localized?.fieldOfWork[option.fieldOfWorkId as string]
                }
                options={availableWorkTitles}
                getOptionLabel={(option) => option.name}
                renderOption={(props, option) => (
                  <ListItem {...props} key={option.id}>
                    <ListItemText>{option.name}</ListItemText>
                  </ListItem>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={t("talentProfile:selectWorkTitles")}
                    size="small"
                    name="workTitles"
                    sx={{ maxWidth: "512px" }}
                    error={!!errors.workTitles}
                  />
                )}
                renderGroup={({ group, children }) => (
                  <AutocompleteGroupedList
                    key={group}
                    group={group}
                    children={children}
                  />
                )}
                onChange={(_, newValue) =>
                  field.onChange(newValue as WorkTitle[])
                }
              />
            )}
          />
        </Grid>
        <Grid
          container
          item
          xs={3}
          justifyContent="flex-end"
          alignItems="center"
        >
          <Typography color="textSecondary">
            {t("talentProfile:reason")}
          </Typography>
        </Grid>
        <Grid item xs={9}>
          <Controller
            control={control}
            name="reason"
            render={({ field }) => (
              <CommonTextField
                {...field}
                disabled={!customerCompany}
                placeholder={t("talentProfile:writeBlacklistReason")}
                sx={{ maxWidth: "512px" }}
                fullWidth
                multiline
                rows={2}
                error={!!errors.reason}
              />
            )}
          />
        </Grid>
      </Grid>
      <Box sx={{ maxWidth: "600px", margin: "16px auto" }}>
        {permissionConflict && (
          <InputHelperText
            errorMessage={t(
              "talentProfile:blacklistSelfServicePermissionConflict",
            )}
          />
        )}
      </Box>
    </>
  );
};
